/**
 * Recursively removes all properties with undefined values from an object.
 * This is necessary because Firestore does not accept undefined as a field value.
 *
 * @param obj - The object to clean
 * @returns A new object with all undefined values removed
 */
export function removeUndefinedFields<T extends Record<string, any>>(
  obj: T,
): Partial<T> {
  if (obj === null || obj === undefined) {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj
      .filter((item) => item !== undefined)
      .map((item) =>
        typeof item === 'object' && item !== null
          ? removeUndefinedFields(item)
          : item,
      ) as unknown as Partial<T>
  }

  if (typeof obj !== 'object') {
    return obj
  }

  const result: Record<string, any> = {}

  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) {
      continue
    }

    if (typeof value === 'object' && value !== null) {
      result[key] = removeUndefinedFields(value)
    } else {
      result[key] = value
    }
  }

  return result as Partial<T>
}
